import { useRef, useState } from "react";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import ContentDetailsModal from "./ContentDetailsModal";
import { useTableState } from "../utils/useTableState";
import api from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import RectangleClassifieds from "../../../creativePreviews/RectangleClassifieds";
import PromotedImagePreview from "../../../creativePreviews/PromotedImagePreview";
import PromotedVideoPreview from "../../../creativePreviews/PromotedVideoPreview";
import HalfVideoPreview from "../../../creativePreviews/HalfVideoPreview";
import { proxyDownload } from "../../../utils/proxyDownload";
import { roles } from "../../../utils/constants";
import Magnifier from "../../../utils/Magnifier";
import Toggle from "../../../utils/Toggle";
import RotatableImage from "../utils/RotatableImage";

const getFileType = (url) => {
  const extension = url.split(".").pop()?.toLowerCase();
  const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
  return videoExtensions.includes(extension) ? "video" : "image";
};
/* ──────────────────────────────────────────────────────────────
   Helper components (FullPreview, ThumbnailPreview, WriteContentForm)
   ────────────────────────────────────────────────────────────── */
const FullPreview = ({ images, currentIndex, setCurrentIndex, isTextEditing, setIsTextEditing, editingText, setEditingText, handleEditText, handleSaveText }) => {
  const current = images[currentIndex];

  const handlePrev = () => setCurrentIndex((p) => (p - 1 + images.length) % images.length);
  const handleNext = () => setCurrentIndex((p) => (p + 1) % images.length);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [isZoomed, setIsZoomed] = useState(false);
  const imgRef = useRef(null);

  const handleMouseMove = (e) => {
    const elem = imgRef.current;
    if (!elem) return;

    const { top, left, width, height } = elem.getBoundingClientRect();
    const x = ((e.pageX - left - window.pageXOffset) / width) * 100;
    const y = ((e.pageY - top - window.pageYOffset) / height) * 100;

    setPosition({ x, y });
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full h-[80vh] gap-4">
        <Toggle open = {showMagnifier} setOpen={setShowMagnifier} title={"Zoom"}/>
        <div className="relative flex items-center justify-center w-full h-[80%] bg-white rounded-lg shadow-md overflow-hidden">
          <button onClick={handlePrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Previous">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="relative bg-white rounded-lg shadow-lg p-2" onMouseMove={handleMouseMove}>
            <RotatableImage
              src={current?.url}
              alt={`creative-${currentIndex}`}
              className="max-w-[90%] max-h-[90%] object-contain"
            />
          </div>
          {showMagnifier ? <Magnifier showMagnifier={showMagnifier} zoomLevel={5} url={current?.url} position={position} /> : null}
          <button onClick={handleNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2" aria-label="Next">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className={`absolute top-2 right-2 w-12 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${current.type === "video" ? "bg-purple-600" : "bg-blue-600"}`}>{current.type === "video" ? "VIDEO" : "IMG"}</div>
        </div>

        <p className="text-xs text-gray-500">
          Media <span className="font-medium">{currentIndex + 1}</span> of {images.length}
        </p>
      </div>

      {isZoomed && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center" onClick={() => setIsZoomed(false)}>
          <img src={current.url} alt={`zoomed-${currentIndex}`} className="max-w-full max-h-full object-contain cursor-zoom-out" />
        </div>
      )}
    </>
  );
};

const ThumbnailPreview = ({ images, currentIndex, setCurrentIndex }) => (
  <div className="flex flex-wrap gap-2 my-2 py-1 bg-gray-50 border-t">
    {images.map((img, idx) => (
      <div key={idx} className="relative">
        <img
          src={img.url}
          alt={`thumb-${idx}`}
          onClick={() => setCurrentIndex(idx)}
          className={`w-12 h-12 object-cover rounded cursor-pointer border transition-transform ${idx === currentIndex ? "border-blue-500 scale-105" : "border-gray-300 hover:border-blue-300"}`}
        />
        <div className={`absolute top-0 right-0 px-1 text-xs font-bold text-white rounded-bl ${img.type === "video" ? "bg-purple-600" : "bg-blue-600"}`}>{img.type === "video" ? "V" : "I"}</div>
      </div>
    ))}
  </div>
);

const WriteContentForm = ({ selectedForm, variations, handleVariationChange, setSelectedForm, adType }) => {
  const max_headline_limit = adType === "promoted_image_story" ? 75 : 32;
  const max_description_limit = adType === "promoted_image_story" ? 400 : 90;
  const show_headline = adType === "promoted_image_story" || adType === "classifiedads";

  return (
    <div className="flex flex-col gap-4 font-lg max-w-[70vh]">
      <div className="flex gap-2">
        {[1, 2, 3].map((n) => (
          <button key={n} className={`px-3 py-1 rounded ${selectedForm === n ? "bg-blue-600 text-white" : "bg-gray-200"}`} onClick={() => setSelectedForm(n)}>
            Variation {n}
          </button>
        ))}
      </div>

      {show_headline && (
        <div>
          <label className="block text-xs text-gray-700">Headline (Max {max_headline_limit} characters)</label>
          <input
            type="text"
            value={variations[selectedForm - 1].headline}
            onChange={(e) => handleVariationChange(selectedForm, "headline", e.target.value)}
            placeholder="Headline"
            className="w-full border px-2 py-1 rounded text-sm"
            maxLength={max_headline_limit}
          />
          <div className="text-right text-green-800">{max_headline_limit - (variations[selectedForm - 1]?.headline?.trim().length ?? 0)} characters</div>
        </div>
      )}

      <div>
        <label className="block font-medium text-gray-700">Description {show_headline && `(Max ${max_description_limit} characters)`}</label>
        <textarea
          value={variations[selectedForm - 1]?.description}
          onChange={(e) => handleVariationChange(selectedForm, "description", e.target.value)}
          placeholder="Description"
          rows={25}
          className="w-full border max-h-[50vh] px-2 py-1 rounded"
          {...(show_headline ? { maxLength: max_description_limit } : {})}
        />
        {show_headline && <div className="text-right text-green-800">{max_description_limit - (variations[selectedForm - 1]?.description?.trim().length ?? 0)} characters</div>}
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   Main component
   ────────────────────────────────────────────────────────────── */
const ContentDetailsCell = ({ creative_id, adDetails}) => {
  /* ────── modal (list of already written content) ────── */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contentData, setContentData] = useState([]);
  const { fetchContent } = useTableState();

  const closeModal = () => setIsModalOpen(false);
  const handleShowContent = async () => {
    const data = await fetchContent(creative_id);
    setContentData(data);
    setIsModalOpen(true);
  };

  /* ────── drawer (write new content) ────── */
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(1);
  const [variations, setVariations] = useState([
    { id: 1, headline: "", description: "" },
    { id: 2, headline: "", description: "" },
    { id: 3, headline: "", description: "" },
  ]);

  const [adData, setAdData] = useState(null);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTextEditing, setIsTextEditing] = useState(false);
  const [editingText, setEditingText] = useState({ url: null, text: "" });
  const [previewMode, setPreviewMode] = useState("full");
  const [isDownloading, setIsDownloading] = useState(false);
  const [content_writer_id, set_content_writer_id] = useState(null);
  const drawerRef = useRef(null);

  /* ────── API helpers ────── */
  const getMedia = async (cid) => {
    const result = await api({
      url: "/alpha_cst_designer/getMedia",
      method: "POST",
      data: { creative_id: cid },
    });
    if (result.status === "SUCCESS") {
      const processed = result.data.map((item) => ({
        url: item.img_url,
        type: getFileType(item.img_url),
      }));
      setMediaUrls(processed);
    }
  };

  const fetchAdData = async (cid) => {
    try {
      const result = await api({
        url: "/user/content_writer/get-ad-details",
        method: "POST",
        data: { creative_id: cid },
      });
      if (result.status === "SUCESS") {
        if (result.data.length === 0) {
          Toast("info", "No Ad Details Found");
        } else {
          set_content_writer_id(result.data[0]?.content_writer_id);
          setAdData(result.data[0]);
          if (result.data[0]?.content?.length) setVariations(result.data[0]?.content);
        }
      }
      await getMedia(cid);
    } catch (error) {
      console.error(error);
      Toast("error", "Failed to load ad details");
    }
  };

  const handleEditText = (url, txt) => {
    setEditingText({ url, text: txt });
  };

  const handleSaveText = async () => {
    if (!editingText.url || !adData) return;
    try {
      const result = await api({
        url: "/user/content_writer/editText",
        method: "POST",
        data: {
          creative_id: adData.creative_id,
          url: editingText.url,
          text: editingText.text,
        },
      });
      if (result.status === "SUCESS") {
        Toast("success", "Text updated");
        setEditingText({ url: null, text: "" });
        setIsTextEditing(false);
        fetchAdData(creative_id);
      }
    } catch {
      Toast("error", "Failed to update text");
    }
  };

  const hasValidContent = () => {
    const type = adData?.ad_creative_sub_type;
    if (type === "promoted_image_story" || type === "classifiedads") return variations.some((v) => v.headline.trim() && v.description.trim());
    return variations.some((v) => v.description.trim());
  };

  const handleUpload = async () => {
    if (!adData || !hasValidContent()) {
      Toast("error", adData?.ad_creative_sub_type === "promoted_image_story" || adData?.ad_creative_sub_type === "classifiedads" ? "All fields required" : "Description required");
      return;
    }
    if (!adData.content_id) {
      Toast("error", "Cannot Find Content Id\n Please Make sure it is Assigned before proceeding");
      return;
    }
    try {
      const result = await api({
        url: "/user/content_writer/uploadContent",
        method: "POST",
        data: {
          content_id: adData.content_id,
          creative_id: adData.creative_id,
          variations,
        },
      });
      if (result.status === "SUCCESS") {
        Toast("success", "Content uploaded");
        setIsDrawerOpen(false);
        // optional: refresh list
        handleShowContent();
      }else{
         Toast('error',result.data?.message || "Failed to upload content")
      }
    } catch(error) {
      Toast("error", error?.message);
    }
  };

  const handleDownloadImages = async (images, name) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const urls = images.map((i) => i.url);
      await proxyDownload(urls, name);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVariationChange = (id, field, value) => {
    if (field === "form") setSelectedForm(value);
    else setVariations((prev) => prev.map((v) => (v.id === id ? { ...v, [field]: value } : v)));
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setAdData(null);
    setMediaUrls([]);
    setVariations([
      { id: 1, headline: "", description: "" },
      { id: 2, headline: "", description: "" },
      { id: 3, headline: "", description: "" },
    ]);
    setCurrentIndex(0);
    setIsTextEditing(false);
    setEditingText({ url: null, text: "" });
  };

  /* ────── open drawer ────── */
  const openWriter = () => {
    setSelectedForm(1);
    fetchAdData(creative_id);
    setIsDrawerOpen(true);
  };
  const role = localStorage.getItem('role');
  return (
    <div className="flex gap-1 flex-wrap items-center">
      {/* Show already-written content */}
      <button onClick={handleShowContent} className="px-2 py-1 rounded-lg bg-gray-200 border whitespace-nowrap">
        Show Content
      </button>

      
      {
        role && role===roles.ops && <button onClick={openWriter} className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors whitespace-nowrap">
          Write Content
        </button>
      }

      {/* Modal with list of existing content */}
      <ContentDetailsModal isOpen={isModalOpen} onClose={closeModal} contentList={contentData} data={adDetails} />

      {/* ────── DRAWER (the whole writer UI) ────── */}
      <SlDrawer ref={drawerRef} label="Ad Preview & Content" style={{ "--size": "100vw" }} placement="end" open={isDrawerOpen} onSlAfterHide={closeDrawer}>
        <div className="flex h-full p-4 gap-4">
          {adData ? (
            (() => {
              const previewProps = {
                img_urls: "",
                video_urls: mediaUrls.find((item) => item.type === "video")?.url || null,
                head_line: adData?.head_line,
                description: adData.description,
                click_url: adData.click_url,
                button_enabled: 0,
                button_link: adData.click_url,
                name: adData.creative_name,
                position: adData.position,
              };

              return (
                <>
                  {/* LEFT – PREVIEW */}
                  <div className="w-2/3 flex flex-col gap-2">
                    {adData.creative_name && <h6>Campaign: {adData.creative_name}</h6>}

                    <button className={`mx-3 px-2 py-1 rounded-lg shadow-md ${isDownloading ? "bg-green-500 cursor-progress" : "bg-blue-400"}`} onClick={() => handleDownloadImages(mediaUrls, adData.creative_name)} disabled={isDownloading}>
                      Download Media
                    </button>

                    {/* Thumbnails */}
                    {mediaUrls.length > 0 && (
                      <ThumbnailPreview images={mediaUrls} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex} />
                    )}

                    {/* PREVIEW AREA */}
                    {mediaUrls.length > 0 && currentIndex !== null ? (
                      <div className="relative">
                        {adData.ad_creative_sub_type === "classified" && mediaUrls[currentIndex].type === "image" ? (
                          <RectangleClassifieds
                            preview={{
                              cla_brand_caption: adData.cla_brand_caption,
                              logo_urls: adData.cla_brand_img,
                              brand_name: adData.cla_brand_name,
                              btn_text_bgclr: adData.cla_btn_bg_clr,
                              btn_icon: adData.cla_btn_icon,
                              button_text: adData.cla_btn_txt,
                              btn_text_clr: adData.cla_btn_txt_col,
                              btn_url: adData.cla_btn_url,
                              no_of_rec: adData.cla_no_of_rec,
                              sub_type: adData.cla_sub_type,
                              img_urls: mediaUrls[currentIndex].url,
                              head_line: adData?.head_line || adData.headline,
                              description: adData.description,
                              button_enabled: !!adData.cla_btn_txt,
                            }}
                          />
                        ) : adData.ad_creative_sub_type === "promoted_video_story" || adData.ad_creative_sub_type === "promoted_image_story" ? (
                          mediaUrls[currentIndex].type === "video" ? (
                            <PromotedVideoPreview preview={{ ...previewProps, img_urls: mediaUrls[currentIndex].url }} />
                          ) : (
                            <PromotedImagePreview preview={{ ...previewProps, img_urls: mediaUrls[currentIndex].url }} />
                          )
                        ) : adData.ad_creative_sub_type === "half_video" ? (
                          <HalfVideoPreview preview={{ ...previewProps, img_urls: mediaUrls[currentIndex].url }} />
                        ) : mediaUrls[currentIndex].type === "image" ? (
                          <FullPreview
                            images={mediaUrls}
                            currentIndex={currentIndex}
                            setCurrentIndex={setCurrentIndex}
                            isTextEditing={isTextEditing}
                            setIsTextEditing={setIsTextEditing}
                            editingText={editingText}
                            setEditingText={setEditingText}
                            handleEditText={handleEditText}
                            handleSaveText={handleSaveText}
                          />
                        ) : (
                          <video
                            src={mediaUrls[currentIndex].url}
                            controls
                            className="w-full h-full object-cover"
                          />
                        )}
                        {mediaUrls.length > 1 && (
                          <>
                            <button onClick={() => setCurrentIndex((p) => (p - 1 + mediaUrls.length) % mediaUrls.length)} className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg z-10" aria-label="Previous">
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                            </button>
                            <button onClick={() => setCurrentIndex((p) => (p + 1) % mediaUrls.length)} className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg z-10" aria-label="Next">
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="font-bold text-lg text-center flex items-center justify-center w-full h-64 border rounded bg-gray-100 text-gray-500">NO MEDIA FOUND</div>
                    )}
                  </div>

                  {/* RIGHT – FORM */}
                  <div className="w-1/3 p-4 bg-gray-50 rounded-lg flex flex-col gap-4">
                    <h4 className="font-semibold">Content Writing</h4>
                    {content_writer_id ? (
                      <>
                        <WriteContentForm selectedForm={selectedForm} variations={variations} setSelectedForm={setSelectedForm} handleVariationChange={handleVariationChange} adType={adData?.ad_creative_sub_type} />
                        <button onClick={handleUpload} className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                          Upload Content
                        </button>
                      </>
                    ) : (
                      <h6 className="px-2 py-1 shadow-md rounded-md">No Content Writer Assigned</h6>
                    )}
                  </div>
                </>
              );
            })()
          ) : (
            <div className="flex items-center justify-center h-full w-full">
              <p className="text-gray-500">Loading…</p>
            </div>
          )}
        </div>
      </SlDrawer>
    </div>
  );
};

export default ContentDetailsCell;
